export type ObservabilityProperties = Record<string, unknown>;
export type ObservabilityValue =
  | string
  | number
  | boolean
  | null
  | ObservabilityValue[]
  | { [key: string]: ObservabilityValue };

const MAX_STRING_LENGTH = 160;
const MAX_ARRAY_LENGTH = 20;
const MAX_OBJECT_KEYS = 30;

const SENSITIVE_KEYS = new Set([
  'authorization',
  'body',
  'checkouturl',
  'companyname',
  'completion',
  'cookie',
  'cvcontent',
  'cvtext',
  'description',
  'email',
  'file',
  'filename',
  'firstname',
  'fullname',
  'lastname',
  'logourl',
  'managementurl',
  'message',
  'messagebody',
  'name',
  'originalname',
  'passport',
  'password',
  'phone',
  'prompt',
  'raw',
  'resume',
  'secret',
  'summary',
  'text',
  'token',
  'transcript',
  'uaepass',
  'url',
  'username',
  'website',
]);

const SENSITIVE_KEY_FRAGMENTS = [
  'access_token',
  'attachment',
  'authorization',
  'avatarurl',
  'checkout_url',
  'cookie',
  'cv_text',
  'download_url',
  'emirates',
  'management_url',
  'refresh_token',
  'resume_text',
  'signed_url',
  'uae_pass',
];

function normalizeKey(key: string) {
  return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function isSensitiveKey(key: string) {
  const normalized = normalizeKey(key);
  if (SENSITIVE_KEYS.has(normalized) || normalized.endsWith('url') || normalized.endsWith('uri')) {
    return true;
  }

  const lower = key.toLowerCase();
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function sanitizeString(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/https?:\/\//i.test(trimmed) || /@/.test(trimmed)) {
    return null;
  }

  return trimmed.length > MAX_STRING_LENGTH
    ? `${trimmed.slice(0, MAX_STRING_LENGTH)}...`
    : trimmed;
}

function sanitizeValue(value: unknown, depth: number): ObservabilityValue | undefined {
  if (value === null) {
    return null;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string') {
    return sanitizeString(value) ?? undefined;
  }

  if (Array.isArray(value)) {
    const sanitized = value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => sanitizeValue(item, depth + 1))
      .filter((item): item is ObservabilityValue => item !== undefined);

    return sanitized.length > 0 ? sanitized : undefined;
  }

  if (isPlainObject(value) && depth < 2) {
    const entries = Object.entries(value)
      .slice(0, MAX_OBJECT_KEYS)
      .filter(([key]) => !isSensitiveKey(key))
      .map(([key, item]) => [key, sanitizeValue(item, depth + 1)] as const)
      .filter((entry): entry is readonly [string, ObservabilityValue] => entry[1] !== undefined);

    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  }

  return undefined;
}

export function sanitizeObservabilityProperties(
  properties?: ObservabilityProperties | null,
) {
  if (!properties) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => !isSensitiveKey(key))
      .map(([key, value]) => [key, sanitizeValue(value, 0)] as const)
      .filter((entry): entry is readonly [string, ObservabilityValue] => entry[1] !== undefined),
  );
}

export function getErrorObservabilityProperties(error: unknown) {
  const record =
    error && typeof error === 'object' ? (error as Record<string, unknown>) : {};
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  const lowerMessage = message.toLowerCase();
  const code =
    typeof record.code === 'string'
      ? record.code
      : typeof record.readableErrorCode === 'string'
        ? record.readableErrorCode
        : isPlainObject(record.userInfo) &&
            typeof record.userInfo.readableErrorCode === 'string'
          ? record.userInfo.readableErrorCode
          : undefined;
  const lowerCode = code?.toLowerCase() ?? '';

  let category = 'unknown';
  if (lowerMessage.includes('cancel')) category = 'cancelled';
  else if (lowerCode.includes('configuration')) category = 'configuration';
  else if (lowerCode.includes('unavailable')) category = 'unavailable';
  else if (lowerMessage.includes('network') || lowerMessage.includes('offline')) category = 'network';
  else if (lowerMessage.includes('timeout')) category = 'timeout';
  else if (lowerMessage.includes('permission') || lowerMessage.includes('unauthorized')) category = 'permission';
  else if (lowerMessage.includes('validation') || lowerMessage.includes('required')) category = 'validation';
  else if (lowerMessage.includes('configuration') || lowerMessage.includes('api key')) category = 'configuration';
  else if (lowerMessage.includes('available') || lowerMessage.includes('missing')) category = 'unavailable';
  else if (lowerMessage.includes('billing') || lowerMessage.includes('purchase')) category = 'billing';

  return sanitizeObservabilityProperties({
    errorName: error instanceof Error ? error.name : typeof error,
    errorCode: code,
    errorCategory: category,
  });
}
